import { NavLink } from 'react-router-dom';
import {
  Home,
  Pencil,
  CheckCircle2,
  Database,
  Target,
  BarChart3,
  Users,
  ShieldCheck,
  Settings,
  HardDriveDownload,
  GraduationCap,
  Activity,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/store/auth';
import { useT } from '@/store/i18n';
import type { TranslationKey } from '@/i18n/translations';
import type { Role } from '@/types';

interface NavItem {
  to: string;
  labelKey: TranslationKey;
  icon: React.ReactNode;
  roles?: Role[];
  groupKey?: TranslationKey;
}

const NAV: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard', icon: <Home className="w-4 h-4" /> },

  { to: '/questions', labelKey: 'nav.my_questions', icon: <Pencil className="w-4 h-4" />, groupKey: 'nav.my_work', roles: ['teacher', 'academic_admin', 'admin'] },
  { to: '/approvals', labelKey: 'nav.approvals', icon: <CheckCircle2 className="w-4 h-4" />, groupKey: 'nav.my_work', roles: ['academic_admin', 'admin'] },

  { to: '/question-bank', labelKey: 'nav.question_bank', icon: <Database className="w-4 h-4" />, groupKey: 'nav.question_bank_section' },
  { to: '/learning-indicators', labelKey: 'nav.learning_indicators', icon: <Target className="w-4 h-4" />, groupKey: 'nav.question_bank_section', roles: ['teacher', 'academic_admin', 'admin'] },
  { to: '/reports', labelKey: 'nav.reports', icon: <BarChart3 className="w-4 h-4" />, groupKey: 'nav.question_bank_section', roles: ['admin', 'academic_admin'] },

  { to: '/users', labelKey: 'nav.users', icon: <Users className="w-4 h-4" />, groupKey: 'nav.system_admin', roles: ['admin'] },
  { to: '/subject-permissions', labelKey: 'nav.subject_permissions', icon: <ShieldCheck className="w-4 h-4" />, groupKey: 'nav.system_admin', roles: ['admin'] },
  { to: '/metadata', labelKey: 'nav.metadata', icon: <Settings className="w-4 h-4" />, groupKey: 'nav.system_admin', roles: ['admin'] },
  { to: '/activity', labelKey: 'nav.activity', icon: <Activity className="w-4 h-4" />, groupKey: 'nav.system_admin', roles: ['admin', 'academic_admin'] },
  { to: '/backup', labelKey: 'nav.backup', icon: <HardDriveDownload className="w-4 h-4" />, groupKey: 'nav.system_admin', roles: ['admin'] },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const role = useAuth((s) => s.user?.role);
  const t = useT();

  const visible = NAV.filter((n) => !n.roles || (role && n.roles.includes(role)));
  const groups = new Map<TranslationKey | undefined, NavItem[]>();
  for (const item of visible) {
    const arr = groups.get(item.groupKey) ?? [];
    arr.push(item);
    groups.set(item.groupKey, arr);
  }

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-slate-900/40 z-40 lg:hidden transition-opacity',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-screen w-64 bg-white border-r border-slate-200 flex flex-col',
          'transition-transform duration-200 lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Brand */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center shadow-sm">
              <GraduationCap className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-slate-900 leading-tight">
                {t('system.title')}
              </div>
              <div className="text-[11px] text-slate-500 truncate">
                {t('system.school')}
              </div>
            </div>
          </div>
          <button
            type="button"
            aria-label={t('sidebar.close_menu')}
            onClick={onClose}
            className="lg:hidden text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {Array.from(groups.entries()).map(([groupKey, items]) => (
            <div key={groupKey ?? '__root'}>
              {groupKey && (
                <div className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {t(groupKey)}
                </div>
              )}
              <ul className="space-y-0.5">
                {items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      onClick={() => {
                        if (window.innerWidth < 1024) onClose();
                      }}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-brand-50 text-brand-700'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        )
                      }
                    >
                      <span className="text-current">{item.icon}</span>
                      <span className="flex-1 truncate">{t(item.labelKey)}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer hint */}
        <div className="border-t border-slate-100 px-4 py-3 text-[11px] text-slate-400">
          v8.0 · {t('sidebar.footer_hint')} <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-slate-600 font-mono">⌘K</kbd> {t('sidebar.footer_search')}
        </div>
      </aside>
    </>
  );
}
